import React, { ReactNode, useState } from 'react';

import FullscreenChart from 'components/Layout/MobileShell/FullscreenChart';
import useIsMobile from 'utils/hooks/useIsMobile';
import { cssVar } from 'constants/theme';

import './index.scss';

export interface GraficoTelaCheiaProps {
  /** Título do gráfico no cabeçalho mobile e no overlay (sem título, só o botão). */
  titulo?: string;
  /**
   * Lado do botão no cabeçalho mobile. `esquerda` para gráficos com rótulo fixo no canto
   * superior direito (ex.: "MAIOR" do `SubsistemaChart`), que o botão cobriria.
   */
  ladoBotao?: 'direita' | 'esquerda';
  /**
   * Recebe se o gráfico está em tela cheia e devolve o gráfico. Em tela cheia o gráfico
   * deve preencher o contêiner (`height: '100%'`, `width: '100%'`).
   */
  children: (telaCheia: boolean) => ReactNode;
}

/**
 * Gráfico dos informes com "Ver em tela cheia" no mobile (padrão do Windx:
 * `ChartComSliders` + `MobileShell/FullscreenChart`). O gráfico vem por render-prop porque a tela
 * cheia é uma **segunda instância**, montada só enquanto o overlay está aberto (a inline sai da
 * árvore, então nunca há dois ECharts vivos).
 *
 * Desktop: devolve `children(false)` puro, sem nenhum nó a mais no DOM. Mobile: cabeçalho claro
 * (nunca navy) com o título opcional e o botão, e o overlay em paisagem (`landscape`).
 */
export default function GraficoTelaCheia({
  titulo,
  ladoBotao = 'direita',
  children,
}: GraficoTelaCheiaProps) {
  const isMobile = useIsMobile();
  const [aberta, setAberta] = useState(false);

  if (!isMobile) return <>{children(false)}</>;

  return (
    <div className="grafico-tela-cheia">
      <div className={`grafico-tela-cheia__cabecalho grafico-tela-cheia__cabecalho--${ladoBotao}`}>
        {titulo && <span className="grafico-tela-cheia__titulo">{titulo}</span>}
        <button
          type="button"
          className="grafico-tela-cheia__expandir"
          aria-label="Ver em tela cheia"
          onClick={() => setAberta(true)}
        >
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke={cssVar('text-strong')}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
          </svg>
        </button>
      </div>

      {!aberta && children(false)}

      <FullscreenChart open={aberta} dismiss={() => setAberta(false)} title={titulo} landscape>
        {aberta && <div className="grafico-tela-cheia__tela">{children(true)}</div>}
      </FullscreenChart>
    </div>
  );
}
